import React from 'react';
import { supportsWebP } from '../../helper/imageOptimization';

// Import original images
import aplusLogoOriginal from '../../assets/aplus-logo-40x40.png';
import mmqrOriginal from '../../assets/mmqr.png';
import ezFrameOriginal from '../../assets/img/EZFrame.png';
import ezBannerOriginal from '../../assets/ezbanner.jpeg';

// Import optimized images
import aplusLogoWebP from '../../assets/optimized/aplus-logo-40x40.webp';
import aplusLogoOptimized from '../../assets/optimized/aplus-logo-40x40.png';
import mmqrWebP from '../../assets/optimized/mmqr.webp';
import mmqrOptimized from '../../assets/optimized/mmqr.png';
import ezFrameWebP from '../../assets/optimized/EZFrame.webp';
import ezFrameOptimized from '../../assets/optimized/EZFrame.png';
import ezBannerWebP from '../../assets/optimized/ezbanner.webp';
import ezBannerOptimized from '../../assets/optimized/ezbanner.jpeg';

interface WebPImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'sync' | 'auto';
  fetchPriority?: 'high' | 'low' | 'auto';
  onLoad?: () => void;
  onError?: () => void;
}

// Map of original images to their optimized versions
const OPTIMIZED_IMAGES: Record<string, { webp: string; optimized: string }> = {
  [aplusLogoOriginal]: {
    webp: aplusLogoWebP,
    optimized: aplusLogoOptimized,
  },
  [mmqrOriginal]: {
    webp: mmqrWebP,
    optimized: mmqrOptimized,
  },
  [ezFrameOriginal]: {
    webp: ezFrameWebP,
    optimized: ezFrameOptimized,
  },
  [ezBannerOriginal]: {
    webp: ezBannerWebP,
    optimized: ezBannerOptimized,
  },
};

// Convert import paths to optimized versions
const getOptimizedImagePath = (originalSrc: string): { webp: string; fallback: string } => {
  const optimized = OPTIMIZED_IMAGES[originalSrc];
  if (optimized) {
    return {
      webp: optimized.webp,
      fallback: optimized.optimized,
    };
  }

  // If no optimized version exists, return original
  return {
    webp: originalSrc,
    fallback: originalSrc,
  };
};

const WebPImage: React.FC<WebPImageProps> = ({
  src,
  alt,
  className = '',
  style,
  loading = 'lazy',
  decoding = 'async',
  fetchPriority = 'auto',
  onLoad,
  onError,
}) => {
  const { webp, fallback } = getOptimizedImagePath(src);
  const hasWebPSupport = supportsWebP();

  // Use WebP if supported and available, otherwise use optimized fallback
  const imageSrc = hasWebPSupport && webp !== src ? webp : fallback;

  return (
    <picture>
      {/* WebP source for supported browsers */}
      {webp !== src && <source srcSet={webp} type="image/webp" />}

      {/* Fallback image */}
      <img
        src={imageSrc}
        alt={alt}
        className={className}
        style={style}
        loading={loading}
        decoding={decoding}
        {...(fetchPriority && { fetchPriority })}
        onLoad={onLoad}
        onError={onError}
      />
    </picture>
  );
};

export default WebPImage;
